import { useState, createContext, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import ClientFormDialog from "@/components/ClientForm";
import TrialExpiredBanner from "@/components/TrialExpiredBanner";
import { useBilling } from "@/context/BillingContext";
import { toast } from "sonner";

const LayoutCtx = createContext({ openAddClient: () => {} });
export const useLayout = () => useContext(LayoutCtx);

const AppLayout = ({ children }) => {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { isLocked } = useBilling();

  const openAddClient = (client = null) => {
    if (isLocked) {
      toast.error("Your trial has ended. Choose a plan to keep adding retainers.");
      navigate("/pricing");
      return;
    }
    setEditing(client);
    setFormOpen(true);
  };

  const handleSaved = () => {
    setRefreshKey((k) => k + 1);
    setFormOpen(false);
    setEditing(null);
  };

  return (
    <LayoutCtx.Provider value={{ openAddClient, refreshKey, isLocked }}>
      <div className="min-h-screen bg-[#F8F6F1]">
        <Sidebar />
        <div className="ml-64">
          <TrialExpiredBanner />
          <Header onAddClient={() => openAddClient()} />
          <main className="p-8 max-w-7xl mx-auto" key={location.pathname}>
            {children}
          </main>
          <footer
            data-testid="app-support-footer"
            className="border-t border-[#E4DFD2] mt-8"
          >
            <div className="max-w-7xl mx-auto px-8 py-5 flex flex-wrap items-center justify-between gap-3 text-xs text-[#69747C]">
              <div>
                Need a hand?{" "}
                <a
                  href="mailto:support@retainertracker.com"
                  data-testid="app-support-email"
                  className="text-[#B89B5E] hover:text-[#1F2326] font-medium"
                >
                  support@retainertracker.com
                </a>
              </div>
              <div className="flex items-center gap-4">
                <a
                  href="/contact"
                  data-testid="app-contact-link"
                  className="hover:text-[#1F2326]"
                >
                  Contact
                </a>
                <a
                  href="/privacy"
                  data-testid="app-privacy-link"
                  className="hover:text-[#1F2326]"
                >
                  Privacy
                </a>
                <a
                  href="/terms"
                  data-testid="app-terms-link"
                  className="hover:text-[#1F2326]"
                >
                  Terms
                </a>
              </div>
            </div>
          </footer>
        </div>
        <ClientFormDialog
          open={formOpen}
          onOpenChange={(o) => {
            setFormOpen(o);
            if (!o) setEditing(null);
          }}
          client={editing}
          onSaved={handleSaved}
        />
      </div>
    </LayoutCtx.Provider>
  );
};

export default AppLayout;
